import * as React from 'react';
import classNames from 'classnames';
import { useController } from 'react-hook-form';

interface CheckboxHighItem {
  name: string;
  label: string;
  description?: string;
}

interface CheckboxHighGroupProps {
  label?: string;
  helpText?: React.ReactNode;
  items: ReadonlyArray<CheckboxHighItem>;
  optionsClassName?: string;
  disabled?: boolean;
  /**
   * Fired after a field's RHF value updates. Lets a caller run a side effect
   * on toggle (e.g. persist + recalculate on the results page) without the
   * group having to know about it.
   */
  onAfterChange?: (name: string, checked: boolean) => void;
}

const fieldInputId = (name: string) => name.replace(/\./g, '-');

/**
 * Stacked `c-form-element--checkbox c-form-element--high` group from the
 * forms cutup. Each item maps to its own boolean RHF field.
 */
export const CheckboxHighGroup: React.VFC<CheckboxHighGroupProps> = ({
  label,
  helpText,
  items,
  optionsClassName = 'c-form-element__options--inline@xs',
  disabled,
  onAfterChange,
}) => {
  return (
    <div className="c-form-element c-form-element--checkbox c-form-element--high">
      {typeof label === 'string' && label.length > 0 && (
        <label className="c-form-label c-form-label--cap">
          <strong>{label}</strong>
        </label>
      )}
      <div className={classNames('c-form-element__options', optionsClassName)}>
        {items.map((item) => (
          <CheckboxHighField key={item.name} item={item} disabled={disabled} onAfterChange={onAfterChange} />
        ))}
      </div>
      {typeof helpText !== 'undefined' && <p className="c-note">{helpText}</p>}
    </div>
  );
};

const CheckboxHighField: React.VFC<{
  item: CheckboxHighItem;
  disabled?: boolean;
  onAfterChange?: (name: string, checked: boolean) => void;
}> = ({ item, disabled, onAfterChange }) => {
  const { field } = useController({ name: item.name });
  const inputId = fieldInputId(item.name);
  const isChecked = field.value === true;

  return (
    <div className="c-form-element__field c-form-element__field--clean">
      <input
        type="checkbox"
        id={inputId}
        checked={isChecked}
        disabled={disabled}
        onChange={(event) => {
          field.onChange(event.target.checked);
          onAfterChange?.(item.name, event.target.checked);
        }}
        onBlur={field.onBlur}
      />
      <label htmlFor={inputId}>
        <div className="c-form-choice c-form-option__head" />
        <div className="c-form-option__body">
          {item.label}
          {typeof item.description === 'string' && item.description.length > 0 && <span>{item.description}</span>}
        </div>
      </label>
    </div>
  );
};

export default CheckboxHighGroup;
